import { RoutingOsrmCalibrationConfig } from '../../../types';

export const DEFAULT_OSRM_CALIBRATION: RoutingOsrmCalibrationConfig = {
    enabled: true,
    shortTripMaxDistanceKm: 3,
    mediumTripMaxDistanceKm: 8,
    shortTripMultiplier: 2.3,
    mediumTripMultiplier: 1.4,
    longTripMultiplier: 1.3,
    fixedDelayMinutes: 1,
    peakHourEnabled: true,
    peakHourMultiplier: 1.1,
    peakMorningStartHour: 7,
    peakMorningEndHour: 9,
    peakEveningStartHour: 17,
    peakEveningEndHour: 19
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const toSafeNumber = (value: number | undefined, fallback: number): number =>
    Number.isFinite(value) ? Number(value) : fallback;

export const normalizeOsrmCalibration = (
    config?: RoutingOsrmCalibrationConfig
): RoutingOsrmCalibrationConfig => {
    const base = config ?? DEFAULT_OSRM_CALIBRATION;

    const shortTripMaxDistanceKm = clamp(toSafeNumber(base.shortTripMaxDistanceKm, DEFAULT_OSRM_CALIBRATION.shortTripMaxDistanceKm), 0.5, 15);
    const mediumTripMaxDistanceKm = clamp(toSafeNumber(base.mediumTripMaxDistanceKm, DEFAULT_OSRM_CALIBRATION.mediumTripMaxDistanceKm), shortTripMaxDistanceKm, 30);

    return {
        enabled: !!base.enabled,
        shortTripMaxDistanceKm,
        mediumTripMaxDistanceKm,
        shortTripMultiplier: clamp(toSafeNumber(base.shortTripMultiplier, DEFAULT_OSRM_CALIBRATION.shortTripMultiplier), 0.8, 3.5),
        mediumTripMultiplier: clamp(toSafeNumber(base.mediumTripMultiplier, DEFAULT_OSRM_CALIBRATION.mediumTripMultiplier), 0.8, 3.5),
        longTripMultiplier: clamp(toSafeNumber(base.longTripMultiplier, DEFAULT_OSRM_CALIBRATION.longTripMultiplier), 0.8, 3.5),
        fixedDelayMinutes: clamp(toSafeNumber(base.fixedDelayMinutes, DEFAULT_OSRM_CALIBRATION.fixedDelayMinutes), 0, 20),
        peakHourEnabled: !!base.peakHourEnabled,
        peakHourMultiplier: clamp(toSafeNumber(base.peakHourMultiplier, DEFAULT_OSRM_CALIBRATION.peakHourMultiplier), 1, 2.5),
        peakMorningStartHour: clamp(Math.round(toSafeNumber(base.peakMorningStartHour, DEFAULT_OSRM_CALIBRATION.peakMorningStartHour)), 0, 23),
        peakMorningEndHour: clamp(Math.round(toSafeNumber(base.peakMorningEndHour, DEFAULT_OSRM_CALIBRATION.peakMorningEndHour)), 0, 23),
        peakEveningStartHour: clamp(Math.round(toSafeNumber(base.peakEveningStartHour, DEFAULT_OSRM_CALIBRATION.peakEveningStartHour)), 0, 23),
        peakEveningEndHour: clamp(Math.round(toSafeNumber(base.peakEveningEndHour, DEFAULT_OSRM_CALIBRATION.peakEveningEndHour)), 0, 23)
    };
};

const isInHourWindow = (hour: number, startHour: number, endHour: number): boolean => {
    if (startHour === endHour) return false;
    if (startHour < endHour) return hour >= startHour && hour < endHour;
    return hour >= startHour || hour < endHour;
};

const getDistanceMultiplier = (distanceKm: number, config: RoutingOsrmCalibrationConfig): number => {
    if (distanceKm <= config.shortTripMaxDistanceKm) return config.shortTripMultiplier;
    if (distanceKm <= config.mediumTripMaxDistanceKm) return config.mediumTripMultiplier;
    return config.longTripMultiplier;
};

export const calibrateOsrmMinutes = (params: {
    rawDurationMins: number;
    distanceKm: number;
    calibration?: RoutingOsrmCalibrationConfig;
    now?: Date;
}): number => {
    const safeRaw = Math.max(0, params.rawDurationMins || 0);
    if (safeRaw === 0) return 0;

    const config = normalizeOsrmCalibration(params.calibration);
    if (!config.enabled) {
        return Math.max(1, safeRaw);
    }

    const safeDistance = Math.max(0, params.distanceKm || 0);
    const now = params.now ?? new Date();

    let calibrated = safeRaw * getDistanceMultiplier(safeDistance, config);

    if (config.peakHourEnabled) {
        const hour = now.getHours();
        const isPeak =
            isInHourWindow(hour, config.peakMorningStartHour, config.peakMorningEndHour) ||
            isInHourWindow(hour, config.peakEveningStartHour, config.peakEveningEndHour);
        if (isPeak) {
            calibrated *= config.peakHourMultiplier;
        }
    }

    calibrated += config.fixedDelayMinutes;

    return Math.max(1, calibrated);
};
